import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase, parseDate, parseTimeTracking, parseRecurrence, formatTaskForSupabase, TEMP_USER_ID } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Json } from '@/integrations/supabase/types';
import type { User } from '@supabase/supabase-js';

export type TaskCategory = 'Personal' | 'Work' | 'Shopping' | 'Health' | 'Other';
export type TaskPriority = 'low' | 'medium' | 'high';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'task' | 'reminder' | 'achievement' | 'system';
  read: boolean;
  createdAt: Date;
}

export interface Task {
  id: string;
  title: string;
  completed: boolean;
  category: TaskCategory;
  priority?: TaskPriority;
  dueDate?: Date;
  description?: string;
  createdAt: Date;
  timeTracking?: {
    totalSeconds: number;
    sessions: { start: Date; end?: Date }[];
  };
  shared?: boolean;
  groupId?: string;
  isRecurring?: boolean;
  recurrence?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number; // every X days/weeks/months
    endDate?: Date;
  };
  reminder?: string;
}

interface TaskUpdateData {
  title?: string;
  description?: string;
  category?: TaskCategory;
  priority?: TaskPriority;
  dueDate?: Date;
  completed?: boolean;
  timeTracking?: {
    totalSeconds: number;
    sessions: { start: Date; end?: Date }[];
  };
  shared?: boolean;
  groupId?: string;
  isRecurring?: boolean;
  recurrence?: {
    frequency: 'daily' | 'weekly' | 'monthly';
    interval: number;
    endDate?: Date;
  };
}

interface AppContextType {
  tasks: Task[];
  addTask: (task: Omit<Task, 'id' | 'createdAt'>) => Promise<void>;
  completeTask: (id: string) => Promise<void>;
  deleteTask: (id: string) => Promise<void>;
  updateTask: (id: string, data: TaskUpdateData) => Promise<void>;
  clearAllTasks: () => Promise<void>;
  onboardingComplete: boolean;
  setOnboardingComplete: (completed: boolean) => void;
  isListening: boolean;
  setIsListening: (isListening: boolean) => void;
  transcript: string;
  setTranscript: (transcript: string) => void;
  processingTranscript: boolean;
  setProcessingTranscript: (processing: boolean) => void;
  shareTask: (id: string) => Promise<void>;
  createRecurringTask: (taskId: string) => Promise<void>;
  loading: boolean;
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  notifications: Notification[];
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => void;
  markNotificationAsRead: (id: string) => void;
  markAllNotificationsAsRead: () => void;
  deleteNotification: (id: string) => void;
  unreadNotificationsCount: number;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

const STORAGE_KEY = 'ai-tasky-todo-data';
const ONBOARDING_KEY = 'ai-tasky-onboarding-complete';

interface AppProviderProps {
  children: React.ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(() => {
    return localStorage.getItem(ONBOARDING_KEY) === 'true';
  });
  
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [processingTranscript, setProcessingTranscript] = useState(false);

  // Calculate unread notifications count
  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  useEffect(() => {
    fetchTasks();
  }, []);

  useEffect(() => {
    localStorage.setItem(ONBOARDING_KEY, onboardingComplete.toString());
  }, [onboardingComplete]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      
      if (!user) {
        setTasks([]);
        return;
      }

      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id);
      
      if (error) {
        console.error('Error fetching tasks:', error);
        toast('Failed to load tasks', {
          description: error.message
        });
        return;
      }
      
      if (data) {
        const transformedTasks: Task[] = data.map(task => ({
          id: task.id,
          title: task.title,
          completed: task.completed,
          category: task.category as TaskCategory,
          priority: task.priority as TaskPriority,
          dueDate: parseDate(task.due_date),
          description: task.description || undefined,
          createdAt: new Date(task.created_at),
          timeTracking: parseTimeTracking(task.time_tracking),
          shared: task.shared,
          groupId: task.group_id,
          isRecurring: task.is_recurring,
          recurrence: parseRecurrence(task.recurrence),
          reminder: task.reminder
        }));
        
        setTasks(transformedTasks);
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      toast('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const addTask = async (task: Omit<Task, 'id' | 'createdAt'>) => {
    try {
      if (!user) {
        toast('Authentication required', {
          description: 'You must be logged in to create tasks'
        });
        return;
      }

      const newTaskData = {
        title: task.title,
        category: task.category,
        priority: task.priority || 'medium',
        description: task.description,
        completed: task.completed || false,
        due_date: task.dueDate ? task.dueDate.toISOString() : null,
        time_tracking: task.timeTracking ? {
          totalSeconds: task.timeTracking.totalSeconds,
          sessions: task.timeTracking.sessions.map(s => ({
            start: s.start.toISOString(),
            end: s.end ? s.end.toISOString() : undefined
          }))
        } : null,
        is_recurring: task.isRecurring || false,
        recurrence: task.recurrence ? {
          frequency: task.recurrence.frequency,
          interval: task.recurrence.interval,
          endDate: task.recurrence.endDate?.toISOString()
        } : null,
        user_id: user.id,
        shared: task.shared || false,
        group_id: task.groupId
      };
      
      const { data, error } = await supabase
        .from('tasks')
        .insert(newTaskData)
        .select();
      
      if (error) {
        console.error('Error adding task:', error);
        toast('Failed to create task', {
          description: error.message
        });
        return;
      }
      
      if (data && data[0]) {
        const createdTask: Task = {
          id: data[0].id,
          title: data[0].title,
          completed: data[0].completed,
          category: data[0].category as TaskCategory,
          priority: data[0].priority as TaskPriority,
          dueDate: parseDate(data[0].due_date),
          description: data[0].description || undefined,
          createdAt: new Date(data[0].created_at),
          timeTracking: parseTimeTracking(data[0].time_tracking),
          shared: data[0].shared,
          groupId: data[0].group_id,
          isRecurring: data[0].is_recurring || false,
          recurrence: parseRecurrence(data[0].recurrence),
          reminder: data[0].reminder
        };
        
        setTasks(prevTasks => [...prevTasks, createdTask]);
        toast('Task created successfully');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      toast('Failed to create task');
    }
  };

  const completeTask = async (id: string) => {
    try {
      const task = tasks.find(t => t.id === id);
      if (!task) return;
      
      const { error } = await supabase
        .from('tasks')
        .update({ completed: !task.completed })
        .eq('id', id);
      
      if (error) {
        console.error('Error updating task:', error);
        return;
      }
      
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === id ? { ...task, completed: !task.completed } : task
        )
      );
      
      if (!task.completed && task.isRecurring) {
        createRecurringTask(id);
      }
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const deleteTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting task:', error);
        return;
      }
      
      setTasks(prevTasks => prevTasks.filter(task => task.id !== id));
    } catch (err) {
      console.error('Error deleting task:', err);
    }
  };
  
  const updateTask = async (id: string, data: TaskUpdateData) => {
    try {
      const updateData: Record<string, any> = {};
      
      if (data.title !== undefined) updateData.title = data.title;
      if (data.description !== undefined) updateData.description = data.description;
      if (data.category !== undefined) updateData.category = data.category;
      if (data.priority !== undefined) updateData.priority = data.priority;
      if (data.dueDate !== undefined) updateData.due_date = data.dueDate;
      if (data.completed !== undefined) updateData.completed = data.completed;
      
      if (data.timeTracking !== undefined) {
        updateData.time_tracking = {
          totalSeconds: data.timeTracking.totalSeconds,
          sessions: data.timeTracking.sessions.map(s => ({
            start: s.start.toISOString(),
            end: s.end ? s.end.toISOString() : undefined
          }))
        };
      }
      
      if (data.shared !== undefined) updateData.shared = data.shared;
      if (data.groupId !== undefined) updateData.group_id = data.groupId;
      if (data.isRecurring !== undefined) updateData.is_recurring = data.isRecurring;
      
      if (data.recurrence !== undefined) {
        updateData.recurrence = {
          frequency: data.recurrence.frequency,
          interval: data.recurrence.interval,
          endDate: data.recurrence.endDate?.toISOString()
        };
      }
      
      const { error } = await supabase
        .from('tasks')
        .update(updateData)
        .eq('id', id);
      
      if (error) {
        console.error('Error updating task:', error);
        return;
      }
      
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === id ? { ...task, ...data } : task
        )
      );
    } catch (err) {
      console.error('Error updating task:', err);
    }
  };
  
  const clearAllTasks = async () => {
    try {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('user_id', TEMP_USER_ID);
      
      if (error) {
        console.error('Error clearing tasks:', error);
        return;
      }
      
      setTasks([]);
    } catch (err) {
      console.error('Error clearing tasks:', err);
    }
  };

  const shareTask = async (id: string) => {
    try {
      const { error } = await supabase
        .from('tasks')
        .update({ shared: true })
        .eq('id', id);
      
      if (error) {
        console.error('Error sharing task:', error);
        return;
      }
      
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === id ? { ...task, shared: true } : task
        )
      );
    } catch (err) {
      console.error('Error sharing task:', err);
    }
  };

  const createRecurringTask = async (taskId: string) => {
    try {
      const originalTask = tasks.find(task => task.id === taskId);
      
      if (!originalTask || !originalTask.isRecurring || !originalTask.recurrence) {
        return;
      }
      
      let nextDueDate: Date | undefined = undefined;
      
      if (originalTask.dueDate) {
        const dueDate = new Date(originalTask.dueDate);
        nextDueDate = new Date(dueDate);
        
        if (originalTask.recurrence.frequency === 'daily') {
          nextDueDate.setDate(dueDate.getDate() + originalTask.recurrence.interval);
        } else if (originalTask.recurrence.frequency === 'weekly') {
          nextDueDate.setDate(dueDate.getDate() + (originalTask.recurrence.interval * 7));
        } else if (originalTask.recurrence.frequency === 'monthly') {
          nextDueDate.setMonth(dueDate.getMonth() + originalTask.recurrence.interval);
        }
      }
      
      if (originalTask.recurrence.endDate && nextDueDate) {
        if (nextDueDate > new Date(originalTask.recurrence.endDate)) {
          return;
        }
      }
      
      const newTaskData = formatTaskForSupabase({
        title: originalTask.title,
        category: originalTask.category,
        priority: originalTask.priority,
        description: originalTask.description,
        dueDate: nextDueDate,
        completed: false,
        is_recurring: true,
        recurrence: originalTask.recurrence,
        timeTracking: {
          totalSeconds: 0,
          sessions: []
        },
        shared: originalTask.shared,
        groupId: originalTask.groupId,
        reminder: originalTask.reminder
      });
      
      const { data, error } = await supabase
        .from('tasks')
        .insert(newTaskData)
        .select();
      
      if (error) {
        console.error('Error creating recurring task:', error);
        return;
      }
      
      if (data && data[0]) {
        const createdTask: Task = {
          id: data[0].id,
          title: data[0].title,
          completed: data[0].completed,
          category: data[0].category as TaskCategory,
          priority: data[0].priority as TaskPriority,
          dueDate: parseDate(data[0].due_date),
          description: data[0].description || undefined,
          createdAt: new Date(data[0].created_at),
          timeTracking: parseTimeTracking(data[0].time_tracking),
          shared: data[0].shared,
          groupId: data[0].group_id,
          isRecurring: data[0].is_recurring || false,
          recurrence: parseRecurrence(data[0].recurrence),
          reminder: data[0].reminder
        };
        
        setTasks(prevTasks => [...prevTasks, createdTask]);
      }
    } catch (err) {
      console.error('Error creating recurring task:', err);
    }
  };

  const addNotification = (notification: Omit<Notification, 'id' | 'createdAt' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: `notification-${Date.now()}`,
      createdAt: new Date(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markNotificationAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const deleteNotification = (id: string) => {
    setNotifications(prev =>
      prev.filter(notification => notification.id !== id)
    );
  };

  // Add notifications for task-related events
  useEffect(() => {
    const handleTaskNotification = (task: Task) => {
      if (task.dueDate) {
        const now = new Date();
        const dueDate = new Date(task.dueDate);
        const timeDiff = dueDate.getTime() - now.getTime();
        const hoursDiff = timeDiff / (1000 * 60 * 60);

        if (hoursDiff <= 24 && hoursDiff > 0) {
          addNotification({
            title: 'Task Due Soon',
            message: `"${task.title}" is due in ${Math.round(hoursDiff)} hours`,
            type: 'task'
          });
        }
      }
    };

    tasks.forEach(handleTaskNotification);
  }, [tasks]);

  const value = {
    tasks,
    setTasks,
    addTask,
    completeTask,
    deleteTask,
    updateTask,
    clearAllTasks,
    onboardingComplete,
    setOnboardingComplete,
    isListening,
    setIsListening,
    transcript,
    setTranscript,
    processingTranscript,
    setProcessingTranscript,
    shareTask,
    createRecurringTask,
    loading,
    user,
    setUser,
    notifications,
    addNotification,
    markNotificationAsRead,
    markAllNotificationsAsRead,
    deleteNotification,
    unreadNotificationsCount,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
